/**
 * Клиент API.
 *
 * Ответы сервера с кодами 409 и 422 — не «ошибки сети», а часть рабочего
 * процесса: конфликт версий при одновременном редактировании и отказ удалить
 * запись со ссылками (§5, §6). Они разбираются в типизированные исключения,
 * чтобы интерфейс мог показать разбор расхождений и понятный текст, а не
 * общее «что-то пошло не так».
 */

import type { ModuleName } from '@otc/shared';

/** Расхождение версий: запись изменил другой пользователь. */
export interface Conflict {
  kind: string;
  id: string;
  expectedVersion: number;
  actualVersion: number | null;
  theirs: Record<string, unknown> | null;
  changedBy: string | null;
  changedAt: string | null;
  message: string;
}

/** Отказ удалить запись из-за ссылок на неё. */
export interface Blocked {
  kind: string;
  id: string;
  reason: 'referenced';
  orderCount: number;
  invoiceNumber?: number;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Запрос не дошёл до сервера: нет сети, сервер не отвечает, оборвалось Wi-Fi.
 *
 * Статус 0, потому что ответа не было вовсе — это отличает такую беду от
 * любого ответа сервера, включая пятисотые: там программа работает и что-то
 * сказала, здесь разговора не состоялось.
 */
export class NetworkError extends ApiError {
  constructor(message = 'Нет связи с сервером. Проверьте подключение к сети') {
    super(0, message);
    this.name = 'NetworkError';
  }
}

/** 401: сессия истекла или пользователь заблокирован. */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Требуется вход в систему') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

/** 409 при коммите модуля: список расхождений для разбора пользователем. */
export class ConflictError extends ApiError {
  constructor(readonly conflicts: Conflict[]) {
    super(409, 'Данные изменены другим пользователем');
    this.name = 'ConflictError';
  }
}

/** 422 при коммите модуля: удаление заблокировано ссылками (§5). */
export class BlockedError extends ApiError {
  constructor(readonly blocked: Blocked[]) {
    super(422, blocked[0]?.message ?? 'Удаление невозможно');
    this.name = 'BlockedError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      // Сессия живёт в httpOnly-cookie, поэтому её нужно отправлять явно.
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    /*
     * Обрыв связи говорит ПО-РУССКИ.
     *
     * Браузер бросает здесь `TypeError: Failed to fetch` — и это сообщение
     * доходило до экрана как есть, потому что показывается текст ошибки.
     * Лаборатории оно не говорит ничего, а пугает: выглядит как поломка
     * программы, хотя означает всего лишь пропавшую сеть.
     *
     * Отмену запроса пропускаем нетронутой: её бросает сама программа, когда
     * уходит с экрана, и ошибкой она не является.
     */
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  const payload = await response.json().catch(() => ({}) as Record<string, unknown>);
  const message = typeof payload.error === 'string' ? payload.error : `Ошибка ${response.status}`;

  if (response.status === 401) throw new UnauthorizedError(message);
  if (response.status === 409 && Array.isArray(payload.conflicts)) {
    throw new ConflictError(payload.conflicts as Conflict[]);
  }
  if (response.status === 422 && Array.isArray(payload.blocked)) {
    throw new BlockedError(payload.blocked as Blocked[]);
  }

  throw new ApiError(response.status, message, payload);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Пакет изменений модуля
// ---------------------------------------------------------------------------

export interface ModuleCommitPayload {
  creates: { tempId: string; kind: string; data: Record<string, unknown> }[];
  updates: { id: string; kind: string; version: number; patch: Record<string, unknown> }[];
  deletes: { id: string; kind: string }[];
}

export interface ModuleCommitResult {
  tempIdMap: Record<string, string>;
  entities: { kind: string; id: string; version: number }[];
  deleted: { kind: string; id: string }[];
}

export function commitModule(module: ModuleName, payload: ModuleCommitPayload): Promise<ModuleCommitResult> {
  return api.post<ModuleCommitResult>(`/api/modules/${module}/commit`, payload);
}

/** Пустой пакет — чтобы не отправлять запрос, когда правок нет. */
export function isEmptyCommit(payload: ModuleCommitPayload): boolean {
  return payload.creates.length === 0 && payload.updates.length === 0 && payload.deletes.length === 0;
}

/** Ссылка на скачивание документа. Открывается в новой вкладке, без fetch. */
export function documentUrl(
  invoiceId: string,
  kind: 'schet' | 'akt',
  format: 'html' | 'pdf' | 'docx',
): string {
  return `/api/invoices/${invoiceId}/document?kind=${kind}&format=${format}`;
}

export function reportExportUrl(reportId: string, format: 'xlsx' | 'pdf' = 'xlsx'): string {
  return `/api/reports/${reportId}/export.${format}`;
}

/**
 * Скачивание ещё НЕ сохранённого отчёта.
 *
 * Обычной ссылкой не обойтись: данные отчёта отправляются телом POST-запроса,
 * а не лежат на сервере. Поэтому файл берётся через fetch и отдаётся браузеру
 * временной ссылкой на blob, которая тут же освобождается.
 *
 * Имя файла берётся из заголовка `Content-Disposition`, который сервер
 * присылает в кодировке RFC 5987 — иначе кириллица в имени превратится
 * в подчёркивания.
 */
/**
 * Скачивание файла по адресу — для телефона.
 *
 * На узком экране ссылка «открыть в новой вкладке» не работает: установленное
 * приложение новых вкладок не открывает вовсе, а мобильный Chrome не умеет
 * показывать PDF внутри себя и всё равно скачивает файл — но уже потеряв
 * заданное имя и оставив пользователя на пустой странице.
 *
 * Забираем файл сами и отдаём его как загрузку: он попадает в «Загрузки» под
 * правильным именем и открывается системной смотрелкой.
 */
export async function downloadFile(url: string, fallbackName: string): Promise<void> {
  /*
   * `no-store` — чтобы браузер не отдал вчерашний документ из своей памяти.
   * Сервер это и так запрещает заголовком, но здесь оно сказано у самого
   * места вызова: документ всегда собирается заново.
   */
  const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      response.status,
      typeof error.error === 'string' ? error.error : 'Не удалось получить документ',
    );
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = disposition.split("filename*=UTF-8''")[1];
  const fileName = encoded ? decodeURIComponent(encoded.split(';')[0]!.trim()) : fallbackName;

  const blobUrl = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/** PDF прайс-листа по листу, отредактированному в окне. */
export async function downloadPriceList(model: unknown): Promise<void> {
  const response = await fetch('/api/price-list/export.pdf', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(model),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(response.status, typeof error.error === 'string' ? error.error : 'Не удалось получить прайс-лист');
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = disposition.split("filename*=UTF-8''")[1];
  const fileName = encoded ? decodeURIComponent(encoded.split(';')[0]!.trim()) : 'Прайс-лист.pdf';
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadReport(payload: unknown, format: 'xlsx' | 'pdf'): Promise<void> {
  const response = await fetch(`/api/reports/export.${format}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      response.status,
      typeof error.error === 'string' ? error.error : 'Не удалось выгрузить отчёт',
    );
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = disposition.split("filename*=UTF-8''")[1];
  const fileName = encoded ? decodeURIComponent(encoded.split(';')[0]!.trim()) : `Наряды.${format}`;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Архив документов по нескольким счетам.
 *
 * Отдельная функция, а не `downloadFile`: список счетов уходит телом запроса,
 * а не в адресе — полсотни ключей в строке адреса упёрлись бы в её предел
 * и обрезались бы молча, без единой ошибки.
 */
export async function downloadInvoiceArchive(
  ids: string[],
  format: 'docx' | 'pdf',
): Promise<void> {
  const response = await fetch('/api/invoices/archive', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids, format }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      response.status,
      typeof error.error === 'string' ? error.error : 'Не удалось собрать архив',
    );
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = disposition.split("filename*=UTF-8''")[1];
  const fileName = encoded
    ? decodeURIComponent(encoded.split(';')[0]!.trim())
    : 'Счета и акты.zip';

  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
